import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Edit, Trash2, Plus, Building, User, Filter, Search,
  ChevronDown, AlertCircle, Loader2, Eye, Globe, RefreshCw,
  CheckCircle, ArrowLeftRight, DollarSign, Calendar, ArrowDown, ArrowUp
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./TransferformList.css";

const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Robust helper to extract a 3‑letter currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const TransferformList = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  // ✅ Get company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  // ✅ Format amount with company currency
  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return formatCurrency(0, companyCurrency);
    return formatCurrency(amount, companyCurrency);
  };

  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [companies, setCompanies] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    by_type: {
      IN: { count: 0, total: 0 },
      OUT: { count: 0, total: 0 }
    }
  });

  // For system admins
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');

  const transferTypes = ["IN", "OUT"];

  useEffect(() => {
    fetchTransfers();
    fetchStats();

    if (isSystemAdmin) {
      fetchCompanies();
    }
  }, [selectedCompany, selectedType, activeOnly]);

  const fetchTransfers = async () => {
    try {
      setLoading(true);

      let endpoint = `${url}/api/transferform`;
      const params = {};

      if (isSystemAdmin && selectedCompany) {
        params.company_id = selectedCompany;
      }

      if (selectedType) {
        params.transfer_type = selectedType;
      }

      if (!activeOnly) {
        params.status = 'all';
      }
      const response = await axios.get(endpoint, { params });
      if (response.data.success) {
        setTransfers(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch transfers");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch transfers");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllTransfers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}/api/transferform/all`);

      if (response.data.success) {
        setTransfers(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch all transfers");
      }
    } catch (error) {
      toast.error("Failed to fetch all transfers");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = {};

      if (isSystemAdmin && selectedCompany) {
        params.company_id = selectedCompany;
      }

      const response = await axios.get(`${url}/api/transferform/stats`, { params });

      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`);

      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleDelete = async (id, transferId) => {
    if (!window.confirm(`Are you sure you want to delete transfer #${transferId}? This will set it to inactive.`)) {
      return;
    }

    try {
      const response = await axios.delete(`${url}/api/transferform/${id}`);

      if (response.data.success) {
        toast.success(`Transfer #${transferId} deleted successfully`);
        fetchTransfers();
        fetchStats();
      } else {
        toast.error(response.data.message || "Failed to delete transfer");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete transfer");
    }
  };

  const handleViewTransfer = (transfer) => {
    const details = `
Transfer Details:

Transfer #: ${transfer.transfer_id}

From Account:
${transfer.fromaccount_id?.account_name || "N/A"}

To Account:
${transfer.toaccount_id?.account_name || "N/A"}

Type:
${transfer.transfer_type === "OUT" ? "Transfer Out" : "Transfer In"}

Amount:
${formatAmount(transfer.amount)}

Date:
${new Date(transfer.transaction_date).toLocaleDateString()}

Description:
${transfer.description || "Not provided"}

Journal No:
${transfer.journalTransaction?.transaction_no || "N/A"}

Accounting Status:
${transfer.accounting_status === "posted" ? "Posted" : "Unposted"}

Company:
${transfer.company?.company_name || "N/A"}

Status:
${transfer.status === "active" ? "Active" : "Inactive"}

Created By:
${transfer.created_by?.name || transfer.created_by?.Usercomp_sure_name || "System"}

Created:
${new Date(transfer.created_at).toLocaleString()}
`;

    alert(details);
  };

  const handleEditTransfer = (id) => {
    navigate(`/transfer/edit/${id}`);
  };

  // Filter transfers based on search term
  const filteredTransfers = transfers.filter(transfer =>
    transfer.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    transfer.fromAccount?.account_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    transfer.toAccount?.account_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    transfer.transfer_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    transfer.company?.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading transfers...</p>
      </div>
    );
  }

  return (
    <div className="transfers-container">
      {/* Header */}
      <div className="header">
        <div>
          <h2>Transfers List</h2>
          <p className="company-info">
            {isSystemAdmin ? (
              <>
                <Globe size={14} /> All Companies
              </>
            ) : (
              <>
                <Building size={14} /> {company?.company_name} ({company?.company_code})
              </>
            )}
          </p>
        </div>

        <div className="header-actions">
          {/* Filter Toggle */}
          <button
            className="filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filter
          </button>

          <button
            onClick={() => navigate("/AddTransfer")}
            className="add-button"
          >
            <Plus size={16} /> New Transfer
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-icon">
            <ArrowLeftRight size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.total || 0}</div>
            <div className="stat-label">Total Transfers</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon in">
            <ArrowDown size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.by_type?.IN?.count || 0}</div>
            <div className="stat-label">IN Transfers</div>
            <div className="stat-amount">{formatAmount(stats.by_type?.IN?.total || 0)}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon out">
            <ArrowUp size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.by_type?.OUT?.count || 0}</div>
            <div className="stat-label">OUT Transfers</div>
            <div className="stat-amount">{formatAmount(stats.by_type?.OUT?.total || 0)}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="filters-container">
          <div className="filter-group">
            <label>Filter by Company:</label>
            <div className="select-wrapper">
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={loadingCompanies || !isSystemAdmin}
              >
                <option value="">All Companies</option>
                {companies.map((comp) => (
                  <option key={comp._id} value={comp._id}>
                    {comp.company_name} ({comp.company_code})
                  </option>
                ))}
              </select>
              <Building size={16} className="select-icon" />
            </div>
          </div>

          <div className="filter-group">
            <label>Filter by Type:</label>
            <div className="select-wrapper">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <option value="">All Types</option>
                {transferTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              <ArrowLeftRight size={16} className="select-icon" />
            </div>
          </div>

          <div className="filter-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              <span>Show Active Only</span>
            </label>
          </div>

          <div className="filter-actions">
            <button
              className="clear-filters"
              onClick={() => {
                setSelectedCompany("");
                setSelectedType("");
                setActiveOnly(true);
              }}
            >
              Clear Filters
            </button>

            <button
              className="apply-filters"
              onClick={() => {
                fetchTransfers();
                setShowFilters(false);
              }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search transfers by description, account names..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button
              className="clear-search"
              onClick={() => setSearchTerm("")}
            >
              ✕
            </button>
          )}
        </div>

        <div className="search-actions">
          <button
            className="refresh-btn"
            onClick={isSystemAdmin ? fetchAllTransfers : fetchTransfers}
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>

          <div className="stats">
            {selectedCompany && (
              <span className="filter-badge">
                <Filter size={12} />
                Filtered
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Transfers Table */}
      <div className="table-container">
        <table className="styled-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Transfer ID</th>
              <th>From Account</th>
              <th>To Account</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Description</th>
              {isSystemAdmin && <th>Company</th>}
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransfers.length === 0 ? (
              <tr>
                <td colSpan={isSystemAdmin ? 11 : 10} className="no-data">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <ArrowLeftRight size={48} color="#ccc" />
                    <p style={{ margin: '15px 0', color: '#666' }}>
                      {searchTerm || selectedCompany || selectedType
                        ? "No transfers found matching your criteria"
                        : "No transfers found"
                      }
                    </p>
                    <button
                      className="add-button"
                      onClick={() => navigate("/AddTransfer")}
                    >
                      <Plus size={16} /> Create First Transfer
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredTransfers.map((transfer, index) => (
                <tr key={transfer._id}>
                  <td>{index + 1}</td>

                  <td>
                    <div className="transfer-id-cell">
                      <ArrowLeftRight size={14} className="mr-2" />
                      <strong>#{transfer.transfer_id}</strong>
                    </div>
                  </td>

                  <td>
                    <div className="account-cell">
                      <div>
                        <div className="account-name">{transfer.fromAccount?.account_name || "N/A"}</div>
                        <small className="account-type">{transfer.fromAccount?.type || ""}</small>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="account-cell">
                      <div>
                        <div className="account-name">{transfer.toAccount?.account_name || "N/A"}</div>
                        <small className="account-type">{transfer.toAccount?.type || ""}</small>
                      </div>
                    </div>
                  </td>

                  <td>
                    <span className={`type-badge ${transfer.transfer_type?.toLowerCase()}`}>
                      {transfer.transfer_type === "IN"
                        ? <ArrowDown size={12} />
                        : <ArrowUp size={12} />
                      }

                      {transfer.transfer_type === "IN"
                        ? "Transfer In"
                        : "Transfer Out"
                      }
                    </span>
                  </td>

                  <td className="amount-cell">
                    <DollarSign size={14} />
                    {formatAmount(transfer.amount)}
                  </td>

                  <td>
                    <div className="date-cell">
                      <Calendar size={12} />
                      {new Date(transfer.transaction_date).toLocaleDateString()}
                    </div>
                  </td>

                  <td>
                    <div className="description-cell">
                      {transfer.description || (
                        <span className="no-description">-</span>
                      )}
                    </div>
                  </td>

                  {isSystemAdmin && (
                    <td>
                      <div className="company-cell">
                        <Building size={12} />
                        <div>
                          <div>{transfer.company?.company_name || "N/A"}</div>
                          <small>{transfer.company?.company_code || ""}</small>
                        </div>
                      </div>
                    </td>
                  )}

                  <td>
                    <span className={`status-badge ${transfer.status === 'active' ? 'active' : 'inactive'}`}>
                      {transfer.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>

                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view-btn"
                        onClick={() => handleViewTransfer(transfer)}
                        title="View Details"
                      >
                        <Eye size={14} />
                      </button>

                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEditTransfer(transfer._id)}
                        title="Edit Transfer"
                        disabled={transfer.status === 'inactive'}
                      >
                        <Edit size={14} />
                      </button>

                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(transfer._id, transfer.transfer_id)}
                        title="Delete Transfer"
                        disabled={transfer.status === 'inactive'}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TransferformList;